import { useState, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import CustomerSidebar from './CustomerSidebar';
import CustomerHeader from './CustomerHeader';
import QRScannerModal from './QRScannerModal';
import { useIsMobile } from '../hooks/useIsMobile';
import { useAuth } from '../context/CustomerAuthContext';
import { bookingsAPI, goalsAPI } from '../services/api';

export default function CustomerLayout() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Prefetch commonly needed data immediately when layout mounts
  // This runs in parallel before any page component renders
  useEffect(() => {
    if (user?.id) {
      // Prefetch bookings (used by Dashboard, Classes, Rings, Sidebar)
      // Key matches what pages use: ['my-bookings', user?.id]
      queryClient.prefetchQuery({
        queryKey: ['my-bookings', user.id],
        queryFn: () => bookingsAPI.getMyBookings(user.id),
        staleTime: 5 * 60 * 1000,
      });

      // Prefetch weekly goal data (used by Dashboard, Rings)
      // Key matches what pages use: ['weekly-goal']
      queryClient.prefetchQuery({
        queryKey: ['weekly-goal'],
        queryFn: () => goalsAPI.getWeeklyGoal(),
        staleTime: 5 * 60 * 1000,
      });
    }
  }, [user?.id, queryClient]);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <div
      className="flex h-screen"
      style={{ background: 'linear-gradient(135deg, var(--bg-base) 0%, var(--bg-surface) 50%, var(--bg-base) 100%)' }}
    >
      {/* Desktop Sidebar - Hidden on Mobile */}
      <div className="hidden md:block">
        <CustomerSidebar />
      </div>

      {/* Mobile Menu Overlay */}
      {isMobile && isMobileMenuOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/70 z-40 backdrop-blur-sm"
            onClick={closeMobileMenu}
          />
          {/* Drawer */}
          <div
            className="fixed left-0 top-0 h-full w-64 z-50 shadow-2xl"
            style={{ background: 'var(--bg-surface)' }}
          >
            <CustomerSidebar onClose={closeMobileMenu} />
          </div>
        </>
      )}

      <div className="flex-1 flex flex-col overflow-hidden">
        <CustomerHeader onMenuClick={toggleMobileMenu} onCheckInClick={() => setShowScanner(true)} />
        <main className="flex-1 overflow-y-auto px-4 md:px-6 pb-4 md:pb-6">
          <Outlet />
        </main>
      </div>

      {/* QR Scanner Modal */}
      <QRScannerModal
        isOpen={showScanner}
        onClose={() => setShowScanner(false)}
        onCheckInSuccess={() => {
          // Force immediate refetch (not just invalidate) so rings page shows fresh data
          queryClient.refetchQueries({ queryKey: ['my-bookings'] });
          queryClient.refetchQueries({ queryKey: ['weekly-goal'] });
        }}
      />
    </div>
  );
}

